// Запросы новой главной (business_apps#1486 §6): география, недавние
// установки, фасеты чипов и настройка варианта пользователя.

import { createAsyncThunk } from '@reduxjs/toolkit'
import i18next from 'i18next'
import API from 'utils/api'
import config from 'config'
import { buildUrl } from 'utils/helpers'
import { showErrorMessage } from 'store/ui/actions'
import { setUserSettings } from 'store/partners/actions'
import {
  RECENT_LIMIT,
  RECENT_PERIOD,
  GEOGRAPHY_CACHE_TTL_MS,
  STORAGE_KEYS,
} from './constants'

const V2_BASE = `${config.redirectUri}/api/v2`

const companyKey = (getState) =>
  API.getActingPartner() || getState().auth?.user?.company?.uuid || 'company'

const geographyCacheKey = (getState) =>
  `${STORAGE_KEYS.geography}:${companyKey(getState)}`

const readGeographyCache = (key) => {
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return null
    const { savedAt, data } = JSON.parse(raw)
    if (!savedAt || Date.now() - savedAt > GEOGRAPHY_CACHE_TTL_MS) return null
    return data
  } catch (error) {
    return null
  }
}

const writeGeographyCache = (key, data) => {
  try {
    window.sessionStorage.setItem(
      key,
      JSON.stringify({ savedAt: Date.now(), data })
    )
  } catch (error) {
    // storage недоступен — живём без кэша
  }
}

const rejectionMessage = async (err) => {
  try {
    const body = await err.json()
    return body?.error || body?.message || String(err.status || 'error')
  } catch (error) {
    return String(err?.message || err?.status || 'error')
  }
}

// GET /api/v2/geography — один запрос при монтировании, кэш в стейте и в
// sessionStorage на 10 минут по компании.
export const fetchGeography = createAsyncThunk(
  'homeV2/fetchGeography',
  async (_, { getState, rejectWithValue }) => {
    const key = geographyCacheKey(getState)
    const cached = readGeographyCache(key)
    if (cached) return cached
    try {
      const payload = await API.request('/geography', {}, V2_BASE)
      writeGeographyCache(key, payload)
      return payload
    } catch (err) {
      return rejectWithValue(await rejectionMessage(err))
    }
  }
)

// GET /api/v2/devices?is_installed=true&installed_since=90d&sort_by=installation_date
// &order=desc&limit=5 — «Недавно установленные»; meta.total — «N за 90 дней».
export const fetchRecent = createAsyncThunk(
  'homeV2/fetchRecent',
  async (_, { rejectWithValue }) => {
    try {
      const { devices, meta } = await API.request(
        buildUrl('/devices', {
          is_installed: true,
          installed_since: RECENT_PERIOD,
          sort_by: 'installation_date',
          order: 'desc',
          limit: RECENT_LIMIT,
          page: 1,
        }),
        {},
        V2_BASE
      )
      return { devices: devices || [], total: Number(meta?.total ?? 0) }
    } catch (err) {
      return rejectWithValue(await rejectionMessage(err))
    }
  }
)

// Ключ набора фильтров, для которого посчитаны фасеты: числа показываются,
// только пока фильтры не изменились.
export const facetsKey = (params = {}) =>
  JSON.stringify(
    Object.keys(params)
      .sort()
      .filter((key) => params[key] !== undefined && params[key] !== null)
      .map((key) => [key, params[key]])
  )

// GET /api/v2/devices/facets?<фильтры> — числа в списке значений чипа,
// запрашиваются при открытии списка с остальными включёнными фильтрами
// (без значений самого чипа). chip — чей это ответ.
export const fetchFacets = createAsyncThunk(
  'homeV2/fetchFacets',
  async ({ params = {} } = {}, { rejectWithValue }) => {
    try {
      return await API.request(buildUrl('/devices/facets', params), {}, V2_BASE)
    } catch (err) {
      return rejectWithValue(await rejectionMessage(err))
    }
  }
)

// PATCH /api/v1/users/me/settings { home_variant } — оптимистично: стор
// переключается сразу, при ошибке возвращаем прежнее значение и показываем Toast.
export const updateUserSettings = createAsyncThunk(
  'homeV2/updateUserSettings',
  async ({ home_variant }, { getState, dispatch, rejectWithValue }) => {
    const { partners } = getState()
    const previous =
      partners?.userSettings || partners?.current?.user_settings || {}
    dispatch(setUserSettings({ ...previous, home_variant }))
    try {
      const { settings } = await API.request('/users/me/settings', {
        method: 'PATCH',
        body: JSON.stringify({ home_variant }),
      })
      dispatch(setUserSettings(settings || { ...previous, home_variant }))
      return settings
    } catch (err) {
      dispatch(setUserSettings(previous))
      dispatch(
        showErrorMessage(
          i18next.t(
            'devices:home_v2.toggle_error',
            'Не удалось сохранить настройку главной'
          )
        )
      )
      return rejectWithValue(await rejectionMessage(err))
    }
  }
)
